import type { PreparedEvmRecovery } from "./onchainRecoveryEvmPublish";
import { bindRecoveryMergeFunding } from "./sdk";
import { ethers } from "ethers";
import type { JoinSplitNote } from "../joinsplit/types";
import { encryptInboxNoteWithPublicKey } from "../utils/inbox-crypto";
import { requestPrivateFees } from "../utils/relayer-client";
import { flatFieldHex } from "./notes";
import { checkPrivateFeeApproval } from "./privateFeeApproval";
import { readFeeNote, type PrivateFeeQuote } from "./privateFeeNotes";
import { PRIVATE_DEPOSIT_MERGE_INTERFACE } from "./privateFeeDepositMergeAbi";
export {
  PRIVATE_DEPOSIT_MERGE_ABI,
  PRIVATE_DEPOSIT_MERGE_INTERFACE,
} from "./privateFeeDepositMergeAbi";

export interface PrivateDepositMergeQuote extends PrivateFeeQuote {
  request: NonNullable<PrivateFeeQuote["request"]> & {
    grossAmount: string;
    treeIndex: number;
    tokenHash: string;
    merkleRoot: string;
    nullifier: string;
    payer: string;
    kind: 5;
    outputTreeIndex: number;
    depositMerge: true;
  };
  depositMerge: {
    family: "deposit-merge-v1";
    payer: string;
    nonce: string;
    deadline: string;
    feeCommitmentsHash: string;
    fundingContext: string;
    verifier: string;
    verifierCodehash: string;
  };
  transaction?: { to: string; data: string; value: string; chainId: number };
}

export interface PrivateDepositMergeData {
  merge: {
    merkleRoot: string;
    nullifier: string;
    commitment: string;
    token: string;
    amount: string;
    deadline: string;
    nonce: string;
    proof: string;
  };
  tokenHash: string;
  feeCommitments: string[];
  feeAmounts: string[];
  feeProofs: string[];
}

function fail(): never {
  throw new Error("Deposit merge authorization does not match this deposit.");
}

export function depositMergeFeeNotes(
  quote: PrivateDepositMergeQuote,
): JoinSplitNote[] {
  const fees = quote.feeOutputs.flatMap((note) =>
    note === null ? [] : [readFeeNote(note)],
  );
  if (
    fees.length > 2 ||
    fees.reduce((sum, note) => sum + note.value, 0n) !==
      BigInt(quote.amounts.totalFee)
  )
    fail();
  return fees;
}

export async function preparePrivateDepositMergeQuote(params: {
  provider: ethers.Provider;
  chainId: bigint;
  pool: string;
  token: string;
  payer: string;
  treeIndex: number;
  tokenHash: string;
  merkleRoot: string;
  nullifier: string;
  grossAmount: bigint;
  signal?: AbortSignal;
}): Promise<PrivateDepositMergeQuote> {
  const quote = await requestPrivateFees<PrivateDepositMergeQuote>(
    "deposit-merge-prepare",
    {
      grossAmount: params.grossAmount.toString(),
      treeIndex: params.treeIndex,
      tokenHash: params.tokenHash,
      token: params.token,
      merkleRoot: params.merkleRoot,
      nullifier: params.nullifier,
    },
    {
      pool: params.pool,
      signal: params.signal,
      requestKey: ethers.hexlify(ethers.randomBytes(32)),
    },
  );
  const contract = new ethers.Contract(
    params.pool,
    [
      "function privateFeeController() view returns(address)",
      "function depositMergeVerifier() view returns(address)",
    ],
    params.provider,
  );
  const [controller, verifier] = await Promise.all([
    contract.privateFeeController(),
    contract.depositMergeVerifier(),
  ]);
  const binding = quote.depositMerge;
  if (
    !binding ||
    binding.family !== "deposit-merge-v1" ||
    quote.chainId !== Number(params.chainId) ||
    ethers.getAddress(quote.pool) !== ethers.getAddress(params.pool) ||
    ethers.getAddress(quote.controller) !== ethers.getAddress(controller) ||
    ethers.getAddress(quote.request?.token ?? ethers.ZeroAddress) !==
      ethers.getAddress(params.token) ||
    quote.request?.depositMerge !== true ||
    quote.request.kind !== 5 ||
    quote.request.treeIndex !== params.treeIndex ||
    quote.request.outputTreeIndex !== params.treeIndex ||
    quote.request.tokenHash?.toLowerCase() !== params.tokenHash.toLowerCase() ||
    quote.request.merkleRoot?.toLowerCase() !==
      params.merkleRoot.toLowerCase() ||
    quote.request.nullifier?.toLowerCase() !== params.nullifier.toLowerCase() ||
    quote.request.grossAmount !== params.grossAmount.toString() ||
    ethers.getAddress(quote.request.payer) !==
      ethers.getAddress(params.payer) ||
    ethers.getAddress(binding.payer) !== ethers.getAddress(params.payer) ||
    ethers.getAddress(binding.verifier) !== ethers.getAddress(verifier) ||
    ethers.keccak256(await params.provider.getCode(verifier)).toLowerCase() !==
      binding.verifierCodehash.toLowerCase() ||
    !ethers.isHexString(quote.id, 32) ||
    binding.nonce.toLowerCase() !== quote.id.toLowerCase() ||
    BigInt(binding.deadline) <= BigInt(Math.floor(Date.now() / 1000)) ||
    BigInt(binding.deadline) > BigInt(Math.floor(Date.now() / 1000) + 3600) ||
    BigInt(quote.amounts.grossAmount) !== params.grossAmount ||
    BigInt(quote.amounts.recipientAmount) <= 0n ||
    BigInt(quote.amounts.recipientAmount) + BigInt(quote.amounts.totalFee) !==
      params.grossAmount ||
    BigInt(quote.amounts.gasReimbursement) !== 0n
  )
    fail();
  const fees = depositMergeFeeNotes(quote);
  if (
    fees.some(
      (note) =>
        note.poolAddress?.toLowerCase() !== params.pool.toLowerCase() ||
        note.treeIndex !== params.treeIndex ||
        flatFieldHex(note.tokenHash) !== params.tokenHash.toLowerCase() ||
        ethers.getAddress(note.tokenData.tokenAddress) !==
          ethers.getAddress(params.token) ||
        note.value <= 0n,
    )
  )
    fail();
  const feeHash = ethers.keccak256(
    ethers.AbiCoder.defaultAbiCoder().encode(
      ["bytes32[]", "uint256[]"],
      [fees.map((note) => note.commitment), fees.map((note) => note.value)],
    ),
  );
  if (binding.feeCommitmentsHash.toLowerCase() !== feeHash.toLowerCase())
    fail();
  checkPrivateFeeApproval(quote, "deposit");
  return quote;
}

export function privateDepositMergeIntentHash(
  quote: Pick<PrivateDepositMergeQuote, "chainId" | "pool" | "amounts">,
  data: PrivateDepositMergeData,
): string {
  const merge = data.merge;
  return ethers.keccak256(
    ethers.AbiCoder.defaultAbiCoder().encode(
      [
        "bytes32",
        "uint256",
        "address",
        "bytes32",
        "bytes32",
        "bytes32",
        "address",
        "uint256",
        "uint256",
        "bytes32",
        "bytes32[]",
        "uint256[]",
        "uint64",
        "bytes32",
      ],
      [
        ethers.id("STEALTHOS_PRIVATE_DEPOSIT_MERGE_INTENT_V1"),
        quote.chainId,
        quote.pool,
        merge.merkleRoot,
        merge.nullifier,
        merge.commitment,
        merge.token,
        quote.amounts.grossAmount,
        merge.amount,
        data.tokenHash,
        data.feeCommitments,
        data.feeAmounts,
        merge.deadline,
        merge.nonce,
      ],
    ),
  );
}

/** A non-executable template still contains all immutable recovery evidence. */
export function privateDepositMergeRecoveryTemplate(
  quote: PrivateDepositMergeQuote,
  data: PrivateDepositMergeData,
): `0x${string}` {
  const authorization = {
    intentHash: ethers.ZeroHash,
    termsHash: quote.termsHash,
    nonce: quote.id,
    issuedAt: 0,
    deadline: quote.depositMerge.deadline,
    kind: 5,
  };
  return (
    data.merge.token === ethers.ZeroAddress
      ? PRIVATE_DEPOSIT_MERGE_INTERFACE.encodeFunctionData(
          "depositAndMergeWithPrivateFees",
          [data, authorization, "0x"],
        )
      : PRIVATE_DEPOSIT_MERGE_INTERFACE.encodeFunctionData(
          "depositTokenAndMergeWithPrivateFees",
          [
            data.merge.token,
            quote.amounts.grossAmount,
            data,
            authorization,
            "0x",
          ],
        )
  ) as `0x${string}`;
}

/** Persist the encrypted owner note before the fee service can see a usable proof. */
export async function authorizePrivateDepositMerge(params: {
  quote: PrivateDepositMergeQuote;
  note: JoinSplitNote;
  recipientMpk: bigint;
  inboxPublicKey: string;
  data: PrivateDepositMergeData;
  publicInputs: string[];
  recovery?: PreparedEvmRecovery | null;
  /** Persist a local input reservation and its encrypted remote journal first. */
  beforeAuthorize: () => Promise<void>;
  signal?: AbortSignal;
}): Promise<`0x${string}`> {
  const { quote, data } = params;
  const recovery = [
    {
      role: "payment",
      commitment: params.note.commitment,
      recipientKey: ethers.sha256(
        ethers.toUtf8Bytes(flatFieldHex(params.recipientMpk)),
      ),
      envelope: await encryptInboxNoteWithPublicKey(
        params.note,
        params.inboxPublicKey,
      ),
    },
  ];
  const backup = await requestPrivateFees<{
    id: string;
    backupReceiptId: string;
    commitment: string;
    fundingContext: string;
  }>(
    "deposit-merge-backup",
    {
      id: quote.id,
      ...(params.recovery ? { recoveryRecords: params.recovery.records } : {}),
      commitment: params.note.commitment,
      outputRecovery: recovery,
    },
    { pool: quote.pool, signal: params.signal },
  );
  if (
    backup.id !== quote.id ||
    !backup.backupReceiptId ||
    backup.commitment.toLowerCase() !== params.note.commitment.toLowerCase() ||
    backup.fundingContext.toLowerCase() !==
      (params.recovery
        ? bindRecoveryMergeFunding(
            quote.depositMerge.fundingContext,
            params.recovery.digest,
          )
        : quote.depositMerge.fundingContext
      ).toLowerCase()
  )
    fail();
  await params.beforeAuthorize();
  if (params.signal?.aborted)
    throw new DOMException("Deposit cancelled", "AbortError");
  const authorized = await requestPrivateFees<PrivateDepositMergeQuote>(
    "deposit-merge-authorize",
    {
      id: quote.id,
      ...(params.recovery ? { recoveryRecords: params.recovery.records } : {}),
      deposit: data,
      publicInputs: params.publicInputs,
      backupReceiptId: backup.backupReceiptId,
    },
    { pool: quote.pool, signal: params.signal },
  );
  const authorization = authorized.authorization;
  if (
    !authorization ||
    authorized.id !== quote.id ||
    authorized.chainId !== quote.chainId ||
    ethers.getAddress(authorized.pool) !== ethers.getAddress(quote.pool) ||
    String(authorization.quote.intentHash).toLowerCase() !==
      privateDepositMergeIntentHash(quote, data).toLowerCase() ||
    String(authorization.quote.termsHash).toLowerCase() !==
      quote.termsHash.toLowerCase() ||
    String(authorization.quote.nonce).toLowerCase() !==
      quote.id.toLowerCase() ||
    BigInt(authorization.quote.deadline) !==
      BigInt(quote.depositMerge.deadline) ||
    BigInt(authorization.quote.issuedAt) <= 0n ||
    BigInt(authorization.quote.issuedAt) >=
      BigInt(authorization.quote.deadline) ||
    BigInt(authorization.quote.kind) !== 5n
  )
    fail();
  const token = ethers.getAddress(data.merge.token);
  const innerCall =
    token === ethers.ZeroAddress
      ? PRIVATE_DEPOSIT_MERGE_INTERFACE.encodeFunctionData(
          "depositAndMergeWithPrivateFees",
          [data, authorization.quote, authorization.signature],
        )
      : PRIVATE_DEPOSIT_MERGE_INTERFACE.encodeFunctionData(
          "depositTokenAndMergeWithPrivateFees",
          [
            token,
            quote.amounts.grossAmount,
            data,
            authorization.quote,
            authorization.signature,
          ],
        );
  const call = params.recovery ? params.recovery.wrap(innerCall) : innerCall;
  const transaction = authorized.transaction;
  if (
    !transaction ||
    ethers.getAddress(transaction.to) !== ethers.getAddress(quote.pool) ||
    transaction.data.toLowerCase() !== call.toLowerCase() ||
    transaction.chainId !== quote.chainId ||
    BigInt(transaction.value) !==
      (token === ethers.ZeroAddress ? BigInt(quote.amounts.grossAmount) : 0n)
  )
    fail();
  return call as `0x${string}`;
}
